import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FaRegEdit } from "react-icons/fa";
import { TbCircleLetterC } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { ISurgeRule, ISurgeRuleFilters } from "@/api_framework/api_modals/SurgePricing";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useSurgePricingAPI } from "@/app/hooks/api_hooks/surge_pricing/useSurgePricingAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import {
  clinicsInCities,
  formatDaysTime,
  formatItems,
  formatUplift,
  formatValidity,
  namesOf,
  pickItem,
  STATUS_ITEMS,
  useSurgeLookups,
  whereNames,
} from "./interface";
import { CARD_CLASS, PageHeader, TruncatedList } from "./components";

const PAGE_SIZE = 20;
const ALL_ITEM: ISelectMenuItemData = { id: "", title: "All" };

interface ISurgeFilterForm {
  city_id: string;
  clinic_id: string;
  service_type_id: string;
  status: string;
}

const cellClassName = "text-xs font-medium text-black whitespace-normal py-4 px-4";
const headerClassName = "text-xs font-semibold text-[#193A6A] py-3.5 px-4";

const SurgePricingList = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { NO_APPOINTMENTS } = useImageConstants();
  const { listRules, isLoading } = useSurgePricingAPI();
  const { cityItems, clinicItems, clinicCity, appointmentTypeItems } = useSurgeLookups({ withPersonas: false });
  const [filters, setFilters] = useState<ISurgeRuleFilters>({ page: 1, page_size: PAGE_SIZE });
  const [rules, setRules] = useState<ISurgeRule[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [canEdit, setCanEdit] = useState<boolean>(false);

  const formHook = useForm<ISurgeFilterForm>({
    mode: "onChange",
    defaultValues: { city_id: "", clinic_id: "", service_type_id: "", status: "" },
  });

  useEffect(() => {
    listRules(filters).then((data) => {
      setRules(data?.rules ?? []);
      setTotal(data?.total ?? 0);
      setCanEdit(Boolean(data?.can_edit));
    });
  }, [filters, listRules]);

  const onboardUrl = getRouteKey("SURGE_PRICING_ONBOARD", "url");
  const onSubmit = (data: ISurgeFilterForm) =>
    setFilters((f) => ({ ...f, ...data, status: data.status as ISurgeRuleFilters["status"], page: 1 }));

  const filterCity = formHook.watch("city_id");
  const filterItems = useMemo(
    () => ({
      city_id: [ALL_ITEM, ...cityItems],
      clinic_id: [ALL_ITEM, ...clinicsInCities(clinicItems, clinicCity, filterCity ? [filterCity] : [])],
      service_type_id: [ALL_ITEM, ...appointmentTypeItems],
      status: [ALL_ITEM, ...STATUS_ITEMS],
    }),
    [cityItems, clinicItems, clinicCity, filterCity, appointmentTypeItems],
  );

  const filterMenu = (name: keyof ISurgeFilterForm, label: string) => {
    const withAll = filterItems[name];
    return (
      <div className="w-full sm:w-[190px]">
        <SearchableSelectMenu
          errorMessages={[]}
          onSelectItem={(item) => {
            formHook.setValue(name, item?.id ?? "");
            if (name === "city_id") formHook.setValue("clinic_id", "");
          }}
          fieldError={formHook.formState.errors[name]}
          register={formHook.register(name)}
          selectItems={withAll}
          placeholder={`Select ${label}`}
          showTooltips={true}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={pickItem(withAll, formHook.watch(name))}
          listBoxClassName="w-full"
          className="text-gray-800"
          label={label}
        />
      </div>
    );
  };

  const columns: TableColumn<ISurgeRule>[] = [
    {
      id: "name",
      header: "Rule",
      minWidth: 170,
      className: cellClassName,
      headerClassName,
      cell: ({ row }) => (
        <div className="flex flex-col items-start gap-1">
          <span className="font-semibold text-gray-900">{row.name}</span>
          <span className="text-[11px] font-normal text-gray-500">{formatValidity(row)}</span>
          {row.overlaps_with?.length ? (
            <SimpleBadge
              label={`Overlaps ${row.overlaps_with.length}`}
              colorClassName="text-[#854D0E] bg-[#FEF9C3] ring-yellow-600/30"
            />
          ) : null}
        </div>
      ),
    },
    {
      id: "types",
      header: "Appointment Types",
      minWidth: 140,
      className: cellClassName,
      headerClassName,
      cell: ({ row }) => <TruncatedList names={namesOf(row.service_type_ids, appointmentTypeItems)} />,
    },
    {
      id: "where",
      header: "Where",
      minWidth: 150,
      className: cellClassName,
      headerClassName,
      cell: ({ row }) => <TruncatedList names={whereNames(row, cityItems, clinicItems)} />,
    },
    { id: "items", header: "Items", minWidth: 130, className: cellClassName, headerClassName, cell: ({ row }) => formatItems(row) },
    {
      id: "uplift",
      header: "Increase",
      className: `${cellClassName} font-semibold text-gray-900`,
      headerClassName,
      cell: ({ row }) => formatUplift(row),
    },
    { id: "when", header: "Days & Time", minWidth: 150, className: cellClassName, headerClassName, cell: ({ row }) => formatDaysTime(row) },
    {
      id: "who",
      header: "Customers",
      minWidth: 120,
      className: cellClassName,
      headerClassName,
      cell: ({ row }) =>
        row.persona.included.length ? <TruncatedList names={row.persona.included} /> : "All customers",
    },
    {
      id: "status",
      header: "Status",
      className: cellClassName,
      headerClassName,
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold ${
            row.status === "ACTIVE"
              ? "bg-green-50 text-green-700 border border-green-100"
              : "bg-red-50 text-red-700 border border-red-100"
          }`}
        >
          {pickItem(STATUS_ITEMS, row.status)?.title}
        </span>
      ),
    },
    {
      id: "action",
      header: "Action",
      align: "center",
      width: 80,
      className: cellClassName,
      headerClassName: `${headerClassName} text-center`,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => navigate(`${onboardUrl}?id=${row.rule_id}`)}
            className="text-indigo-600 hover:text-indigo-900 transition-colors p-1.5 hover:bg-indigo-50 rounded-lg"
          >
            <FaRegEdit className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Surge Pricing"
        subtitle="Raises the clinic price of services and BOMs for chosen appointment types. With no matching rule, the clinic price is used."
        actions={
          <>
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => navigate(getRouteKey("SURGE_PRICING_PRICE_CHECK", "url"))}
            >
              Price Check
            </button>
            {canEdit && (
              <button type="button" className={PRIMARY_BUTTON} onClick={() => navigate(onboardUrl)}>
                + Create Rule
              </button>
            )}
          </>
        }
      />

      <div className={`${CARD_CLASS} px-4 sm:px-6 py-4`}>
        <form onSubmit={formHook.handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            {filterMenu("city_id", "City")}
            {filterMenu("clinic_id", "Clinic")}
            {filterMenu("service_type_id", "Appointment Type")}
            {filterMenu("status", "Status")}
            <button type="submit" className={PRIMARY_BUTTON}>
              Search
            </button>
            <div className="w-full sm:ml-auto sm:w-64">
              <InputWithAddOn
                onEnterKeyPress={(query: string) => setFilters((f) => ({ ...f, search: query, page: 1 }))}
                containerClassName="w-full"
                addon={<TbCircleLetterC className="w-4 h-4 text-gray-500" />}
                placeholder="Search by Rule Name"
              />
            </div>
          </div>
        </form>
      </div>

      <div className={`${CARD_CLASS} px-4 sm:px-6 py-4`}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">Rules</h3>
          <span className="text-xs text-gray-500">{total} total</span>
        </div>
        {isLoading ? (
          <TableSkeleton />
        ) : rules.length === 0 ? (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No rules found." />
        ) : (
          <>
            <TableComponent<ISurgeRule>
              columns={columns}
              data={rules}
              getRowId={(row) => row.rule_id}
              containerClassName="border border-gray-200/80 shadow-sm rounded-2xl mr-0 bg-white"
              scrollContainerClassName="overflow-x-auto mt-0 max-h-[calc(100vh-320px)]"
              minTableWidthClassName="min-w-full"
              tableClassName="min-w-full divide-y divide-gray-200"
              stickyHeader={true}
              wrapCells={true}
            />
            <div className="my-4 flex items-center justify-center">
              <PaginationComponent
                className="pagination-bar"
                currentPage={filters.page ?? 1}
                pageSize={PAGE_SIZE}
                totalCount={total}
                onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SurgePricingList;
